
import React from "react";
import { 
  Tag, Receipt, Barcode, MapPin, Building2, Package2, Copy 
} from "lucide-react";
import { InventoryItem, getMagazijnLabel } from "@/types/inventory";
import { Button } from "@/components/ui/button";
import { copyToClipboard, copyAllProductDetails, copyBasicProductDetails } from "@/utils/clipboard";
import { MOQDisplay } from "./MOQDisplay";

interface InventoryItemDetailsProps {
  item: InventoryItem;
}

export const InventoryItemDetails: React.FC<InventoryItemDetailsProps> = ({ item }) => {
  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 text-lg">
        <Tag className={`h-5 w-5 ${item.voorraad > 0 ? 'text-green-500' : 'text-red-500'}`} />
        <span className="font-bold flex-1">
          {item.product_naam}{item.eenheid ? ` · ${item.eenheid}` : ''}
        </span>
        <Button 
          variant="ghost" 
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => copyToClipboard(item.product_naam, "Productnaam")}
        >
          <Copy className="h-4 w-4" />
          <span className="sr-only">Kopieer productnaam</span>
        </Button>
      </div>
      <div className="mt-2 space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          <span>Artikelnummer: </span>
          <span className="font-bold">{item.product_id}</span>
          <Button 
            variant="ghost" 
            size="sm"
            className="h-6 w-6 p-0 ml-auto"
            onClick={() => copyToClipboard(item.product_id, "Artikelnummer")}
          >
            <Copy className="h-3 w-3" />
            <span className="sr-only">Kopieer artikelnummer</span>
          </Button>
        </div>
        {item.bc && (
          <div className="flex items-center gap-2">
            <Barcode className="h-4 w-4" />
            <span>Scancode: </span>
            <span className="font-bold">{item.bc}</span>
            <Button 
              variant="ghost" 
              size="sm"
              className="h-6 w-6 p-0 ml-auto"
              onClick={() => copyToClipboard(item.bc, "Scancode")}
            >
              <Copy className="h-3 w-3" />
              <span className="sr-only">Kopieer scancode</span>
            </Button>
          </div>
        )}
        {item.locatie && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>Locatie: </span>
            <span className="font-bold">{item.locatie}</span>
            <Button 
              variant="ghost" 
              size="sm"
              className="h-6 w-6 p-0 ml-auto"
              onClick={() => copyToClipboard(item.locatie, "Locatie")}
            >
              <Copy className="h-3 w-3" />
              <span className="sr-only">Kopieer locatie</span>
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Building2 className={`h-4 w-4 ${item.magazijn === 2 ? 'text-blue-500' : ''}`} />
          <span>Magazijn: </span>
          <span className={`font-bold ${item.magazijn === 2 ? 'text-blue-500' : ''}`}>
            {getMagazijnLabel(item.magazijn)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Package2 className={`h-4 w-4 ${item.voorraad > 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span>Voorraad: </span>
          <span className={`font-bold ${item.voorraad > 0 ? 'text-green-500' : 'text-red-500'}`}>{item.voorraad}</span>
        </div>
        <MOQDisplay moq={item.moq} showIcon showLabel />
        
        {/* Copy buttons */}
        <div className="pt-2 grid grid-cols-2 gap-2">
          <Button
            variant="copy"
            size="sm"
            className="flex items-center justify-center gap-2"
            onClick={() => copyBasicProductDetails(item)}
          >
            <Copy className="h-4 w-4" />
            <span>Kopieer basis</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="flex items-center justify-center gap-2"
            onClick={() => copyAllProductDetails(item)}
          >
            <Copy className="h-4 w-4" />
            <span>Kopieer alles</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
